'use client'

import { useEffect, useState } from 'react'
import { ordinal, type Heading } from '@/lib/portfolio'

/**
 * The table of contents beside a body, built from its heading blocks: which
 * section you are reading, and how far through you are.
 *
 * Both come from one scroll listener over the sections already in the DOM,
 * rather than from an observer per heading — the reading percentage needs the
 * distance between the first and the last section anyway, which an
 * IntersectionObserver does not give you.
 */
export function ArticleToc({ headings }: { headings: Heading[] }) {
    const [current, setCurrent] = useState(headings[0]?.anchor ?? '')
    const [read, setRead] = useState(0)

    useEffect(() => {
        let ticking = false

        const measure = () => {
            const elements = headings
                .map((heading) => document.getElementById(heading.anchor))
                .filter((element): element is HTMLElement => element !== null)

            if (elements.length === 0) {
                return
            }

            let active = elements[0].id

            for (const element of elements) {
                // A section counts as the one being read once its heading has
                // passed the upper third of the window.
                if (element.getBoundingClientRect().top <= window.innerHeight * 0.36) {
                    active = element.id
                }
            }

            const first = elements[0].getBoundingClientRect()
            const last = elements[elements.length - 1].getBoundingClientRect()
            const total = Math.max(1, last.bottom - first.top)
            const progress = Math.min(1, Math.max(0, (window.innerHeight * 0.5 - first.top) / total))

            setCurrent(active)
            setRead(progress)
        }

        const onScroll = () => {
            if (ticking) {
                return
            }

            ticking = true

            requestAnimationFrame(() => {
                ticking = false
                measure()
            })
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        measure()

        return () => window.removeEventListener('scroll', onScroll)
    }, [headings])

    return (
        <nav
            aria-label="Inhoudsopgave"
            className="sticky top-27 px-gutter py-14 pr-[34px] max-lg:static max-lg:py-8"
        >
            <span className="mb-5 block font-mono text-[10.5px] tracking-[0.08em] text-faint">
                IN DIT ARTIKEL
            </span>
            <div className="flex flex-col">
                {headings.map((heading, index) => {
                    const active = heading.anchor === current

                    return (
                        <a
                            key={heading.anchor}
                            href={`#${heading.anchor}`}
                            aria-current={active ? 'true' : undefined}
                            className={`relative grid grid-cols-[30px_1fr] items-baseline gap-3 py-[9px] pl-3.5 transition-[color,padding-left] duration-300 ease-design hover:pl-5 hover:text-fg ${
                                active ? 'text-fg' : 'text-faint'
                            }`}
                        >
                            <span
                                aria-hidden="true"
                                className={`absolute top-2 bottom-2 left-0 block w-px origin-left transition-[background,transform] duration-400 ease-design ${
                                    active ? 'scale-x-200 bg-accent' : 'bg-hair'
                                }`}
                            />
                            <span className="font-mono text-[10.5px] opacity-70">{ordinal(index)}</span>
                            <span className="text-[15.5px] leading-snug tracking-[-0.01em]">
                                {heading.title}
                            </span>
                        </a>
                    )
                })}
            </div>

            <div className="mt-[26px] flex items-center gap-3 border-t border-hair pt-[18px]">
                <span className="relative block h-0.5 flex-1 overflow-hidden bg-hair">
                    <span
                        aria-hidden="true"
                        className="absolute inset-y-0 left-0 block bg-accent"
                        style={{ width: `${(read * 100).toFixed(1)}%` }}
                    />
                </span>
                <span className="flex-none font-mono text-[10px] text-faint">{Math.round(read * 100)}%</span>
            </div>
        </nav>
    )
}
